import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import cn from 'classnames'
import { ListFilter, X } from 'lucide-react'
import api from 'api/api'
import queryClient from 'api/queryClient'
import { removeMutedStream } from 'utils/muteUtils'
import styles from './public-feed-settings-button.module.scss'

export default function PublicFeedSettingsButton({
    className,
    label = null,
    iconSize = 16,
    showHideAuctionPosts = false,
    archivedOnly = false,
    onArchivedOnlyChange,
}) {
    const [showPopover, setShowPopover] = useState(false)
    const buttonRef = useRef(null)

    const handleButtonClick = ev => {
        ev.stopPropagation()
        setShowPopover(current => !current)
    }

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={cn(styles.triggerButton, className)}
                onClick={handleButtonClick}
                title="Manage channel filters"
                aria-label="Manage channel filters"
                aria-expanded={showPopover}
            >
                <ListFilter size={iconSize} />
                {label && <span>{label}</span>}
            </button>
            {showPopover &&
                ReactDOM.createPortal(
                    <PublicFeedSettingsPopover
                        buttonRef={buttonRef}
                        onClose={() => setShowPopover(false)}
                        showHideAuctionPosts={showHideAuctionPosts}
                        archivedOnly={archivedOnly}
                        onArchivedOnlyChange={onArchivedOnlyChange}
                    />,
                    document.body,
                )}
        </>
    )
}

function PublicFeedSettingsPopover({
    buttonRef,
    onClose,
    showHideAuctionPosts,
    archivedOnly,
    onArchivedOnlyChange,
}) {
    const [mutedStreams, setMutedStreams] = useState([])
    const [loading, setLoading] = useState(true)
    const [hideAuctionPosts, setHideAuctionPosts] = useState(gon.hideAuctionPosts === true)
    const [isSavingPreference, setIsSavingPreference] = useState(false)
    const popoverRef = useRef(null)

    useEffect(() => {
        const fetchMutedStreams = async () => {
            const streams = await api.getMutedStreams()
            setMutedStreams(streams)
            setLoading(false)
        }

        fetchMutedStreams()
    }, [])

    useEffect(() => {
        const handleClickOutside = event => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [buttonRef, onClose])

    const handleUnmute = async streamId => {
        const response = await api.unmuteStream(streamId)
        if (response.ok) {
            removeMutedStream(streamId)
            setMutedStreams(current => current.filter(stream => stream.stream_id !== streamId))
        }
    }

    const handleHideAuctionPostsChange = async event => {
        const nextValue = event.target.checked
        const previousValue = hideAuctionPosts

        setHideAuctionPosts(nextValue)
        setIsSavingPreference(true)

        try {
            const response = await api.updateUserOptions({ hideAuctionPosts: nextValue })
            if (!response?.ok) {
                setHideAuctionPosts(previousValue)
                return
            }

            gon.hideAuctionPosts = nextValue
            if (gon.currentUser) {
                gon.currentUser.options = {
                    ...(gon.currentUser.options || {}),
                    hideAuctionPosts: nextValue,
                }
            }

            await queryClient.invalidateQueries({ queryKey: ['quests'] })
        } catch (error) {
            setHideAuctionPosts(previousValue)
        } finally {
            setIsSavingPreference(false)
        }
    }

    const handleArchivedPostsChange = event => {
        onArchivedOnlyChange?.(event.target.checked)
    }

    const buttonRect = buttonRef.current?.getBoundingClientRect()
    const popoverStyle = buttonRect
        ? {
              position: 'absolute',
              top: buttonRect.bottom + 8,
              right: window.innerWidth - buttonRect.right,
              zIndex: 1000,
          }
        : {}

    return (
        // @ts-expect-error - popoverStyle has literal string 'absolute' which TS narrows incorrectly for CSSProperties
        <div ref={popoverRef} className={styles.mutedStreamsPopover} style={popoverStyle}>
            <div className={styles.popoverHeader}>
                <span>Channel filters</span>
                <button className={styles.closeButton} onClick={onClose} type="button">
                    <X size={16} />
                </button>
            </div>
            <div className={styles.popoverContent}>
                <div className={styles.preferenceSection}>
                    <div className={styles.sectionTitle}>Filter posts</div>
                    {showHideAuctionPosts && (
                        <>
                            <label className={styles.preferenceRow}>
                                <span className={styles.preferenceCopy}>
                                    <span className={styles.preferenceHeading}>
                                        <span className={styles.preferenceLabel}>
                                            Hide auction posts
                                        </span>
                                        <span className={styles.betaPill}>Beta</span>
                                    </span>
                                    <span className={styles.preferenceHelp}>
                                        Hide NFT auction posts in Public, Following, and search
                                        using AI.
                                    </span>
                                </span>
                                <input
                                    type="checkbox"
                                    checked={hideAuctionPosts}
                                    onChange={handleHideAuctionPostsChange}
                                    disabled={isSavingPreference}
                                />
                            </label>
                            {isSavingPreference && (
                                <div className={styles.preferenceStatus}>Saving preference...</div>
                            )}
                        </>
                    )}
                    <label className={styles.preferenceRow}>
                        <span className={styles.preferenceCopy}>
                            <span className={styles.preferenceHeading}>
                                <span className={styles.preferenceLabel}>Show archived posts</span>
                            </span>
                            <span className={styles.preferenceHelp}>
                                Show only threads whose title is archived.
                            </span>
                        </span>
                        <input
                            type="checkbox"
                            checked={archivedOnly === true}
                            onChange={handleArchivedPostsChange}
                        />
                    </label>
                </div>
                <div className={styles.sectionDivider} />
                <div className={styles.preferenceSection}>
                    <div className={styles.sectionTitle}>Muted channels</div>
                    {loading ? (
                        <div className={styles.loadingText}>Loading...</div>
                    ) : mutedStreams.length === 0 ? (
                        <div className={styles.emptyText}>No muted channels yet.</div>
                    ) : (
                        <ul className={styles.streamList}>
                            {mutedStreams.map(stream => (
                                <li key={stream.stream_id} className={styles.streamItem}>
                                    <span className={styles.streamName}>{stream.stream_name}</span>
                                    <button
                                        className={styles.unmuteButton}
                                        onClick={() => handleUnmute(stream.stream_id)}
                                        type="button"
                                    >
                                        Unmute
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    )
}
